import React from 'react';
import {
  Badge,
  Banner,
  Brief,
  CandidateTaxonomyUtil,
  CandidateExperienceTaxonomyDetails,
  CondensedHeader,
  CondensedLarge,
  ConfirmSubmitDialog,
  DateUtil,
  Gutter,
  IconButton,
  Layout,
  LayoutItem,
  Paragraph,
  ParagraphHeader,
  ReadonlyTextarea,
  Flashy,
  Icon,
} from '@axiom/ui';
import type {
  Candidate,
  CandidateProfileIssue,
  Experience,
  Taxonomy,
} from '@axiom/validation';
import { PositionsConst } from '@axiom/const';
import { DateUtils } from '@axiom/utils';

import { ExperienceApi } from '../../api/experience-api';
import { CandidateExperienceApi } from '../../api/candidate-experience-api';
import { CandidateApi } from '../../api/candidate-api';
import { CandidateProfileIssuesApi } from '../../api/candidate-profile-issues-api';
import { CandidateProfileHighlightsApi } from '../../api/candidate-profile-highlights-api';
import { CandidateProfileTopFocusAreasApi } from '../../api/candidate-profile-top-focus-areas-api';

import { ExperiencesBannerPrompt } from './ExperiencesBannerPrompt';
import { ExperiencesShortBanner } from './ExperiencesShortBanner';

const { EndDateStatuses } = PositionsConst;
export type ExperiencesItemDisplayProps = {
  candidateId: Candidate['id'];
  experience: Experience;
  taxonomy: Taxonomy;
  showMergeTip: boolean;
  issues: CandidateProfileIssue[];
  onExperienceNav: () => void;
  name?: string;
};

export const ExperiencesItemDisplay = ({
  candidateId,
  experience,
  taxonomy,
  showMergeTip,
  issues,
  onExperienceNav,
  name = 'EXPERIENCE_ITEM_DISPLAY',
}: ExperiencesItemDisplayProps) => {
  const { industries } = taxonomy;
  const deleteExperience = async () => {
    await ExperienceApi.deleteExperience(experience.id);

    await CandidateApi.refreshCandidate(candidateId);
    await CandidateProfileIssuesApi.refreshCandidateProfileIssues(candidateId);
    await CandidateProfileHighlightsApi.refreshProfileHighlights(candidateId);
    await CandidateProfileTopFocusAreasApi.refreshProfileTopFocusAreas(
      candidateId
    );

    await CandidateExperienceApi.refreshExperiences(candidateId);
    return CandidateExperienceApi.refreshGroupedExperiences(candidateId);
  };

  const showShortExperienceBanner =
    experience.isAxiom &&
    experience.endDate !== null &&
    experience.opportunity?.position?.endDateStatus ===
      EndDateStatuses.Confirmed &&
    DateUtils.getDaysBetween(experience.startDate, experience.endDate) < 90;

  return (
    <div data-test={name}>
      <Layout position="middle">
        <LayoutItem fluid>
          {experience.isAxiom ? (
            <Gutter bottom="8px">
              <Badge background="orange" name="AXIOM_EXPERIENCE_BADGE">
                Axiom
              </Badge>
            </Gutter>
          ) : null}
          <ParagraphHeader name="CLIENT">{experience.client}</ParagraphHeader>
        </LayoutItem>
        <LayoutItem>
          <Layout horizontalGutter="8px" position="middle">
            {!experience.isAxiom && (
              <ConfirmSubmitDialog
                name="CONFIRM_DELETE_EXPERIENCE_DIALOG"
                yesButtonText="Delete"
                size="tiny"
                modalTitle="Delete This Experience"
                modalBody="Deleting this experience removes it from your Axiom for Talent Profile and the bio that Axiom shares with prospective clients."
                Trigger={
                  <IconButton
                    icon="trash"
                    pattern="secondary"
                    variation="minimal"
                    name="DELETE_EXPERIENCE_ICON"
                  />
                }
                onYes={deleteExperience}
              />
            )}
            <IconButton
              name="EDIT_EXPERIENCE_BUTTON"
              icon="pencil"
              pattern="secondary"
              variation="minimal"
              onClick={() => {
                onExperienceNav();
              }}
            />
          </Layout>
        </LayoutItem>
      </Layout>
      <Gutter bottom="8px" />
      {!experience.isAxiom && (
        <Paragraph name="EXTERNAL_OPPORTUNITY_NAME">
          {experience.externalOpportunityName || '--'}
        </Paragraph>
      )}
      <Paragraph name="DATE_RANGE">
        {DateUtil.displayDateRange(experience.startDate, experience.endDate)}
      </Paragraph>
      <Paragraph name="LOCATION">
        {experience.isAxiom
          ? experience.opportunity?.locationCityStateCountry
          : experience.locationName}
      </Paragraph>
      <Gutter bottom="16px" />
      {showShortExperienceBanner ? (
        <ExperiencesShortBanner experienceId={experience.id} />
      ) : null}
      <ExperiencesBannerPrompt
        issues={issues}
        experience={experience}
        onOpenEdit={() => {
          onExperienceNav();
        }}
      />
      {!experience.isAxiom &&
        /axiom/i.test(
          `${experience.client} ${experience.externalOpportunityName}`
        ) &&
        !experience.suggestAxiomEngageDismissedAt && (
          <>
            <Banner name="CONVERT_EXPERIENCE_HINT">
              <Layout>
                <LayoutItem>
                  <ParagraphHeader as="span">
                    Should this be an Axiom experience?
                  </ParagraphHeader>{' '}
                  It looks like this might be an engagement you had with Axiom
                  in the past. Your Talent Success Partner can assist in marking
                  it as an Axiom experience if needed.
                </LayoutItem>
                <LayoutItem>
                  <IconButton
                    icon="x-mark"
                    name="CONVERT_EXPERIENCE_HINT_DISMISS"
                    onClick={async () => {
                      await ExperienceApi.updateExperience(experience.id, {
                        suggestAxiomEngageDismissedAt:
                          DateUtil.formatAsTimestamp(new Date()),
                      });
                      await CandidateExperienceApi.refreshExperiences(
                        candidateId
                      );
                    }}
                    pattern="secondary"
                    variation="minimal"
                  />
                </LayoutItem>
              </Layout>
            </Banner>
            <Gutter bottom="16px" />
          </>
        )}
      {showMergeTip ? (
        <Gutter bottom="16px">
          <Banner name="MERGE_EXPERIENCE_HINT">
            <Layout>
              <LayoutItem>
                <ParagraphHeader as="span">
                  Did you do the same work at both of these engagements?
                </ParagraphHeader>{' '}
                If so, you should consider merging them. Reach out to your
                talent partner to let them know which experience should be kept.
              </LayoutItem>
              <LayoutItem>
                <IconButton
                  icon="x-mark"
                  name="MERGE_EXPERIENCE_HINT_DISMISS"
                  onClick={async () => {
                    await ExperienceApi.updateExperience(experience.id, {
                      suggestMergeDismissedAt: DateUtil.formatAsTimestamp(
                        new Date()
                      ),
                    });
                    await CandidateExperienceApi.refreshExperiences(
                      candidateId
                    );
                  }}
                  pattern="secondary"
                  variation="minimal"
                />
              </LayoutItem>
            </Layout>
          </Banner>
        </Gutter>
      ) : null}
      {experience.exceptionRequestedAt && !experience.exceptionApprovedAt ? (
        <Flashy color="dataVizYellow">
          <Layout
            horizontalGutter="8px"
            position="right middle"
            name="EXPERIENCE_DESCRIPTION_EXCEPTION_REQUESTED"
          >
            <Flashy color="textDefault">Exception requested</Flashy>
            <Icon name="circle-waiting-filled" />
          </Layout>
        </Flashy>
      ) : null}
      <Brief
        briefMaxHeight="96px"
        pattern="secondary"
        key={`${experience.suggestMergeDismissedAt}${experience.suggestAxiomEngageDismissedAt}`}
      >
        <ReadonlyTextarea
          value={experience.description || '--'}
          name="DESCRIPTION"
          formatting="modern"
        />
      </Brief>
      {CandidateTaxonomyUtil.showExperienceTaxonomy(experience) ? (
        <Gutter top="16px">
          <CandidateExperienceTaxonomyDetails
            experience={experience}
            taxonomy={taxonomy}
          />
        </Gutter>
      ) : null}
      {!!experience.industryValue && (
        <Gutter top="16px">
          <CondensedHeader>Industry</CondensedHeader>
          <Gutter bottom="8px" />
          <CondensedLarge name="INDUSTRY">
            {industries[experience.industryValue]?.displayValue}
          </CondensedLarge>
        </Gutter>
      )}
    </div>
  );
};
